import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/http/client'
import type { ApiResponse } from '@/api/http/types'
import { queryKeys } from '@/api/query-keys'
import { SP_NOTIFICATIONS_API_BASE } from '../lib/notifications-api-paths'
import { parseNotificationMessage } from '../lib/parse-notification-message'
import {
  isDevNotificationBackendDisabled,
  isNotificationUnavailableError,
  markDevNotificationBackendUnavailable,
  notificationDevQueryOptions,
} from '../lib/notificationDevBackend'

export const PANEL_NOTIFICATION_PAGE_SIZE = 10
export const FULL_PAGE_NOTIFICATION_SIZE = 20

export interface BellNotificationItem {
  id: string
  message: string
  title?: string
  description?: string
  relatedUrl?: string | null
  isRead?: boolean
  createdAt?: string | null
}

export interface BellNotificationListMeta {
  total: number
  page: number
  per_page: number
  total_pages: number
  unreadCount: number
  readCount: number
}

export interface BellNotificationApiRow {
  id?: string
  _id?: string
  message?: string
  title?: string
  description?: string
  relatedUrl?: string | null
  isRead?: boolean
  createdAt?: string | Date | null
}

export function mapNotificationRow(row: BellNotificationApiRow): BellNotificationItem | null {
  const id = String(row.id ?? row._id ?? '').trim()
  if (!id) return null

  const message = row.message?.trim() ?? ''
  const parsed = parseNotificationMessage(message)
  const apiTitle = row.title?.trim()
  const apiDescription = row.description?.trim()
  let createdAt: string | null = null
  if (row.createdAt != null) {
    createdAt =
      typeof row.createdAt === 'string'
        ? row.createdAt
        : new Date(row.createdAt).toISOString()
  }

  return {
    id,
    message,
    title: apiTitle || parsed.title,
    description: apiDescription || parsed.description,
    relatedUrl: row.relatedUrl ?? null,
    isRead: Boolean(row.isRead),
    createdAt,
  }
}

const notificationRetry = (failureCount: number, error: unknown) => {
  if (isNotificationUnavailableError(error)) {
    markDevNotificationBackendUnavailable(error)
    return false
  }
  return import.meta.env.DEV ? false : failureCount < 2
}

export function useBellUnreadCount() {
  return useQuery({
    queryKey: queryKeys.spNotifications.unreadCount(),
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<{ count: number }>>(
        `${SP_NOTIFICATIONS_API_BASE}/unread-count`,
      )
      return data.data.count
    },
    ...notificationDevQueryOptions,
    throwOnError: false,
    meta: { suppressGlobalError: true },
    retry: notificationRetry,
  })
}

export function useBellPanelNotifications() {
  return useQuery({
    queryKey: queryKeys.spNotifications.panelList(),
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<BellNotificationApiRow[]>>(
        SP_NOTIFICATIONS_API_BASE,
        { params: { page: 1, pageSize: PANEL_NOTIFICATION_PAGE_SIZE } },
      )
      return data.data
        .map(mapNotificationRow)
        .filter((row): row is BellNotificationItem => row !== null)
    },
    enabled: notificationDevQueryOptions.enabled() && !isDevNotificationBackendDisabled(),
    throwOnError: false,
    meta: { suppressGlobalError: true },
    retry: notificationRetry,
  })
}

/** @deprecated Use useBellPanelNotifications — kept for test mocks */
export const useBellNotifications = useBellPanelNotifications
